import { Button, ButtonProps } from '@mantine/core';
import React, { forwardRef, useCallback } from 'react';

import {
  useSendPartnerSupportEmail,
  UseSendPartnerSupportEmailParams,
} from '@portals/api/organizations';
import { useOpenModal } from '@portals/redux';

import { ContactSupportModalProps } from '../modals';

interface ContactPartnerSupportButtonProps extends ButtonProps {
  supportModalTitle: string;
  partnerId?: string;
}

export const ContactPartnerSupportButton = forwardRef<
  HTMLButtonElement,
  ContactPartnerSupportButtonProps
>(({ supportModalTitle, partnerId, children, ...buttonProps }, ref) => {
  const openContactPartnerSupportModal = useOpenContactPartnerSupportModal();

  return (
    <Button
      ref={ref}
      color="blue_gray.8"
      variant="subtle"
      {...buttonProps}
      onClick={() =>
        openContactPartnerSupportModal({
          title: supportModalTitle,
          partnerId: partnerId,
        })
      }
      data-testid="contact-support-button"
    >
      {children || 'Contact support'}
    </Button>
  );
});

export function useOpenContactPartnerSupportModal(params?: {
  onOpenContactSupportModal?: VoidFunction;
}) {
  const openModal = useOpenModal();

  const { mutate: sendPartnerSupportEmail } = useSendPartnerSupportEmail();

  const onSendSupportEmail = useCallback(
    (values: UseSendPartnerSupportEmailParams) => {
      sendPartnerSupportEmail(
        {
          title: values.title,
          message: values.message,
          partnerId: values.partnerId,
        },
        {
          onSuccess: () => {
            openModal('ContactSupportSuccessModal');
          },
        }
      );
    },
    [openModal, sendPartnerSupportEmail]
  );

  return useCallback(
    (payload?: { title?: string; message?: string; partnerId?: string }) => {
      params?.onOpenContactSupportModal?.();

      openModal<ContactSupportModalProps['data']>('ContactSupportModal', {
        onSubmit: onSendSupportEmail,
        title: payload?.title,
        message: payload?.message,
        partnerId: payload?.partnerId,
      });
    },
    [onSendSupportEmail, openModal, params]
  );
}
